// High-level send/receive protocol — pure functions

import { type Result, ok, err } from './types.js';
import { POW_BITS } from './constants.js';
import { hash, concat, sign as cryptoSign, verify as cryptoVerify, encrypt, decrypt } from './crypto.js';
import { findValidNonce } from './pow.js';
import { validatePoW } from './pow.js';
import { computeRoomTag, computeSenderTag, findSender } from './tags.js';
import { type WireMessage, buildPlaintext, parsePlaintext } from './message.js';
import { type RoomState, advanceHead } from './room.js';
import { getPublicKey } from './crypto.js';

/**
 * Send a message in a room.
 *
 * 1. Find a valid nonce (PoW grind)
 * 2. Compute room tag and sender tag
 * 3. Sign: sign(privkey, hash(nonce || currentHead || message))
 * 4. Build padded plaintext
 * 5. Derive symmetric key: hash(sharedSecret || nonce)
 * 6. Encrypt plaintext
 * 7. Assemble wire message
 */
export const sendMessage = (
  room: RoomState,
  message: Uint8Array,
  senderPrivateKey: Uint8Array,
  powBits: number = POW_BITS,
): Result<Readonly<{ wireMessage: WireMessage; nonce: Uint8Array }>, string> => {
  try {
    // 1. PoW grind
    const nonce = findValidNonce(room.roomId, room.currentHead, powBits);

    // 2. Compute tags
    const roomTag = computeRoomTag(room.roomId, nonce, room.currentHead);
    const senderPublicKey = getPublicKey(senderPrivateKey);
    const senderTag = computeSenderTag(senderPublicKey, nonce);

    // 3. Sign
    const sigInput = hash(concat(nonce, room.currentHead, message));
    const signature = cryptoSign(senderPrivateKey, sigInput);

    // 4. Build plaintext
    const plaintextResult = buildPlaintext(message, signature);
    if (!plaintextResult.ok) return plaintextResult;

    // 5. Derive symmetric key
    const symKey = hash(concat(room.sharedSecret, nonce));

    // 6. Encrypt
    const encryptResult = encrypt(symKey, plaintextResult.value);
    if (!encryptResult.ok) return encryptResult;

    // 7. Assemble
    const wireMessage: WireMessage = Object.freeze({
      senderTag,
      roomTag,
      nonce,
      ciphertext: encryptResult.value,
    });

    return ok(Object.freeze({ wireMessage, nonce }));
  } catch (e) {
    return err(e instanceof Error ? e.message : String(e));
  }
};

/**
 * Receive a message in a room.
 *
 * 1. Validate PoW on room tag
 * 2. Try each head (current + recent) to find matching room tag
 * 3. Derive symmetric key, decrypt
 * 4. Parse plaintext
 * 5. Find sender from member list
 * 6. Verify signature
 * 7. Advance head, return results
 */
export const receiveMessage = (
  room: RoomState,
  wireMessage: WireMessage,
  powBits: number = POW_BITS,
): Result<Readonly<{ sender: Uint8Array | null; message: Uint8Array; nonce: Uint8Array; updatedRoom: RoomState }>, string> => {
  try {
    // 1. Validate PoW
    if (!validatePoW(wireMessage.roomTag, powBits)) {
      return err('Proof-of-work validation failed');
    }

    // 2. Find matching head
    const allHeads = [room.currentHead, ...room.recentHeads];
    const matchedHead = allHeads.find((head) => {
      const candidateTag = computeRoomTag(room.roomId, wireMessage.nonce, head);
      return candidateTag.length === wireMessage.roomTag.length &&
        candidateTag.every((byte, i) => byte === wireMessage.roomTag[i]);
    });

    if (matchedHead === undefined) {
      return err('No matching head found for room tag');
    }

    // 3. Derive symmetric key and decrypt
    const symKey = hash(concat(room.sharedSecret, wireMessage.nonce));
    const decryptResult = decrypt(symKey, wireMessage.ciphertext);
    if (!decryptResult.ok) return decryptResult;

    // 4. Parse plaintext
    const parseResult = parsePlaintext(decryptResult.value);
    if (!parseResult.ok) return parseResult;

    const { message, signature } = parseResult.value;

    // 5. Find sender (permissive — unknown senders accepted without signature verification)
    const senderResult = findSender(room.members, wireMessage.senderTag, wireMessage.nonce);
    const sender = senderResult.ok ? senderResult.value : null;

    // 6. Verify signature (only if sender is known)
    if (sender !== null) {
      const sigInput = hash(concat(wireMessage.nonce, matchedHead, message));
      const valid = cryptoVerify(sender, sigInput, signature);
      if (!valid) {
        return err('Signature verification failed');
      }
    }

    // 7. Advance head
    const updatedRoom = advanceHead(room, message);

    return ok(Object.freeze({ sender, message, nonce: wireMessage.nonce, updatedRoom }));
  } catch (e) {
    return err(e instanceof Error ? e.message : String(e));
  }
};
